/**
 * /plants — Public Plant Directory (Brief A · CHANGE 6 + 9)
 *
 * Lists field-captured plant signups (MongoDB `field_signups`), distinct from
 * the paid manufacturer profiles at /directory. Search bar, horizontal-scroll
 * filter chips (mobile friendly), responsive grid (2 col desktop / 1 col mobile),
 * "Get Notified" waitlist modal when results are empty, sort options.
 *
 * Mobile field-use optimizations:
 *  - min 44px tap targets · 16px input fonts (no iOS zoom)
 *  - filter chips horizontally scrollable (no wrap)
 *  - search bar auto-focused on mount
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, ArrowRight } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";
import useSeoHead from "../useSeoHead";
import { CompanyDirectory, groupCompanies } from "../components/CompanyDirectory";

export default function Plants() {
  const { t, lang } = useI18n();
  const [activeCap, setActiveCap] = useState("All");
  const [allCaps, setAllCaps] = useState([]);
  const [showAllChips, setShowAllChips] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWaitlist, setShowWaitlist] = useState(false);

  /* Brief SEO V2 · #1 + #3 — Plants directory SEO head */
  useSeoHead({
    title:
      lang === "es"
        ? "Directorio de Plantas IMMEX · Juárez | Corridor Digital"
        : "IMMEX Plant Directory · Juárez | Corridor Digital",
    description:
      lang === "es"
        ? "Fabricantes IMMEX del corredor Juárez–El Paso. Basado en el listado oficial IMMEX y evidencia de investigación citada. Búsqueda directa y RFQ sin intermediarios."
        : "IMMEX manufacturers on the Juárez–El Paso corridor. Based on the official IMMEX listing and cited research evidence. Direct search and RFQ with no middlemen.",
    path: "/plants",
    lang,
    jsonLd:
      items && items.length > 0
        ? [
            {
              id: "ld-plants-itemlist",
              payload: {
                "@context": "https://schema.org",
                "@type": "ItemList",
                numberOfItems: items.length,
                itemListElement: items.slice(0, 30).map((p, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  url: `https://corridordigital.net/m/${p.slug}`,
                  name: p.display_name || p.company_name || p.legal_entity || p.slug,
                })),
              },
            },
          ]
        : [],
  });

  /**
 * Load raw manufacturer documents. The card renderer now reads the full
 * document (all backfilled IMMEX fields) so we no longer adapt to a
 * lossy shape. Kept as-is what the API already returns.
 */
async function load() {
    setLoading(true);
    try {
      // iter48 — bumped from limit:500 (was cutting off 578 of 1,078
      // published rows, mostly IMMEX plants that sort last) to limit:2000.
      // Backend has no hard cap — this returns every published plant in
      // one call so the "N plantas / N plants" header is truthful.
      const params = { limit: 2000 };
      // Exact taxonomy-category membership (shared evidence-based source) —
      // never a fuzzy/regex match, so results equal the /c/{slug} page.
      if (activeCap !== "All") params.capability_exact = activeCap;
      const { data } = await api.get("/manufacturers", { params });
      const raw = Array.isArray(data) ? data : data.items || [];
      // Pass raw documents through — PlantCard reads the full field set,
      // including backfilled plant_location_id, plant_address, IMMEX
      // reference/year, colonia, postal_code, assigned_park, evidence_state,
      // etc. No adapter, no field-dropping.
      setItems(raw);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    // Shared 81-category taxonomy with live counts — same source as
    // Directory rail, Digest index and /c/{slug} pages.
    (async () => {
      try {
        const { data } = await api.get("/public/capabilities");
        setAllCaps(data);
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    // re-load when the selected category changes (taps are discrete)
    load();
    // eslint-disable-next-line
  }, [activeCap]);

  const visible = useMemo(() => items, [items]);
  const companyCount = useMemo(() => groupCompanies(visible).length, [visible]);
  const isEmpty = !loading && visible.length === 0;

  return (
    <div data-testid="plants-page" className="pb-24">
      <section
        className="container-cd pt-12 md:pt-16 pb-6"
        style={{ borderBottom: "1px solid var(--line)" }}
      >
        <div className="overline">
          CORRIDOR · MFG · {companyCount.toLocaleString()} {lang === "es" ? "empresas" : "companies"} · {visible.length.toLocaleString()} {lang === "es" ? "ubicaciones de planta" : "plant locations"}
        </div>
        <h1
          className="font-display mt-4 leading-[0.9]"
          style={{ fontSize: "clamp(36px, 6vw, 72px)" }}
          data-testid="plants-title"
        >
          {t("plants.title")}
        </h1>
        <p
          className="mt-4 text-base md:text-lg max-w-2xl"
          style={{ color: "var(--ink-2)" }}
          data-testid="plants-subtitle"
        >
          {t("plants.subtitle")}
        </p>

        {/* Category chips — the exact 81-category approved taxonomy, single
            select, exact match. Zero-result categories stay available. */}
        <div className="mt-5" data-testid="plants-chips">
          <div className="flex flex-wrap gap-2">
            {[{ name: "All", slug: "all", count: null }, ...(showAllChips ? allCaps : allCaps.slice(0, 11))].map((c) => {
              const chip = c.name;
              const active = activeCap === chip;
              return (
                <button
                  key={chip}
                  onClick={() => setActiveCap(chip)}
                  className="font-mono text-xs whitespace-nowrap"
                  style={{
                    padding: "10px 14px",
                    minHeight: 44,
                    border: "1px solid var(--ink)",
                    background: active ? "var(--ink)" : "white",
                    color: active ? "white" : "var(--ink)",
                  }}
                  data-testid={`plants-chip-${chip.replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                >
                  {chip === "All" ? t("plants.filter.all") : chip}
                  {c.count !== null && c.count !== undefined ? ` · ${c.count}` : ""}
                </button>
              );
            })}
            {allCaps.length > 11 && (
              <button
                onClick={() => setShowAllChips((v) => !v)}
                className="font-mono text-xs whitespace-nowrap"
                style={{
                  padding: "10px 14px",
                  minHeight: 44,
                  border: "1px dashed var(--ink)",
                  background: "white",
                  color: "var(--ink)",
                }}
                data-testid="plants-chips-toggle-all"
              >
                {showAllChips
                  ? lang === "es" ? "Mostrar menos" : "Show fewer"
                  : lang === "es"
                    ? `Ver las ${allCaps.length} categorías →`
                    : `Show all ${allCaps.length} categories →`}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="container-cd py-10">
        {loading ? (
          <div className="font-mono text-sm overline" data-testid="plants-loading">
            {t("common.loading")}
          </div>
        ) : isEmpty ? (
          <div className="cd-card p-10 md:p-14 text-center" data-testid="plants-empty">
            <div className="font-display text-2xl md:text-3xl">{t("plants.empty.title")}</div>
            <p className="mt-3 text-sm" style={{ color: "var(--ink-2)" }}>
              {t("plants.empty.cta")}.
            </p>
            <button
              onClick={() => setShowWaitlist(true)}
              className="btn btn-primary btn-lg mt-6"
              style={{ minHeight: 48 }}
              data-testid="plants-get-notified-btn"
            >
              {t("plants.empty.cta")} <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <CompanyDirectory items={visible} lang={lang} />
        )}
      </section>

      {showWaitlist && (
        <WaitlistModal
          defaultCapability={activeCap !== "All" ? activeCap : ""}
          capabilityOptions={allCaps.map((c) => c.name)}
          onClose={() => setShowWaitlist(false)}
        />
      )}
    </div>
  );
}

function WaitlistModal({ defaultCapability, capabilityOptions = [], onClose }) {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [capability, setCapability] = useState(defaultCapability || capabilityOptions[0] || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await api.post("/buyer-waitlist", { email, capability });
      setDone(true);
    } catch (e2) {
      setErr(
        e2.response?.data?.detail ||
          (lang === "es" ? "No se pudo guardar." : "Couldn't save.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          zIndex: 200,
        }}
        data-testid="waitlist-overlay"
      />
      <div
        role="dialog"
        className="bg-white"
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          maxWidth: 520,
          margin: "0 auto",
          padding: "24px 20px 32px",
          borderRadius: "16px 16px 0 0",
          zIndex: 210,
          border: "1px solid var(--line)",
        }}
        data-testid="waitlist-modal"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{ position: "absolute", top: 12, right: 12, minWidth: 32, minHeight: 32 }}
          data-testid="waitlist-close"
        >
          <X size={18} />
        </button>
        {done ? (
          <div className="text-center py-6" data-testid="waitlist-success">
            <div className="font-display text-2xl">{t("plants.waitlist.success")}</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="font-display text-2xl">{t("plants.waitlist.title")}</div>
            <div>
              <label className="overline">{t("plants.waitlist.email")}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="cd-input mt-2 w-full"
                style={{ fontSize: 16, minHeight: 48 }}
                data-testid="waitlist-email"
              />
            </div>
            <div>
              <label className="overline">{t("dir.filter.capability")}</label>
              <select
                value={capability}
                onChange={(e) => setCapability(e.target.value)}
                className="cd-input mt-2 w-full"
                style={{ fontSize: 16, minHeight: 48 }}
                data-testid="waitlist-capability"
              >
                {capabilityOptions.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            {err && (
              <div className="text-sm" style={{ color: "var(--red, #dc2626)" }} data-testid="waitlist-error">
                {String(err)}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="btn btn-primary w-full"
              style={{ minHeight: 48 }}
              data-testid="waitlist-submit"
            >
              {submitting ? t("common.loading") : t("plants.waitlist.submit")}
            </button>
          </form>
        )}
      </div>
    </>
  );
}
